import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { addDays } from 'date-fns';
import { RootReducer } from '@store/index';
import OrdersService from '@api/Orders/OrderCtrl';
import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import { subscribeOrdersWindow } from '@api/Orders/OrdersWindowSubscription';
import { filterBoardOrders, hasPaidDeliveryOrders, sortBoardOrders } from '@screens/Orders/boardOrders';
import { playNotificationSound } from '@utils/Sounds';
import useTimer from '@hooks/useTimer';
import useDelayedOrdersNotifier from '@hooks/useDelayedOrdersNotifier';

/**
 * El inicio de HOY. ⚠️ Era `const today = new Date()` **a nivel de módulo**, o
 * sea evaluado al cargar el chunk: el tablero de cocina vive abierto todo el día
 * en una tablet que no se recarga, así que al cruzar la medianoche seguía
 * escuchando la ventana de AYER y los pedidos nuevos no aparecían. Se calcula al
 * montar, y el reloj de la pantalla lo empuja al día siguiente.
 */
const startOfToday = (now: Date = new Date()): Date => {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start;
};

export interface OrdersDateRange {
    startDate: Date;
    endDate: Date;
}

/**
 * Los pedidos de `/orders`: la ventana realtime de la sucursal, los filtros de
 * pantalla (rango, buscador, ocultar los de mucho tiempo) y los avisos que salen
 * de los datos (sonido de pedido nuevo, pedido retrasado).
 *
 * Lecturas: la primera entrega de la ventana trae el día completo de la sucursal
 * (~180 pedidos) y después solo lo que cambia; la ventana se comparte entre
 * montajes (`subscribeOrdersWindow`), así que ir al POS y volver no la repaga.
 * Teclear en el buscador o mover el interruptor NO lee nada: se filtra en memoria.
 */
const useOrdersFeed = (ordersService: OrdersService) => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    // Órdenes tal como llegan del listener (la ventana del día de la sucursal,
    // ya ordenadas). Lo que se VE (`orders`) se deriva abajo con los filtros de
    // pantalla; así teclear en el buscador NO recrea el listener (recrearlo es
    // volver a pagar toda la ventana del día — cada lectura de Firestore se cobra).
    const [rawOrders, setRawOrders] = useState<Order[]>([]);
    const [search, setSearch] = useState<string>('');
    const [hideTodayLongTime, setHideTodayLongTime] = useState<boolean>(true);
    const [onSearch] = useDebounce(search, 500);
    // El día se fija al MONTAR (ver `startOfToday`), no al cargar el bundle.
    const montadoHoy = useMemo(() => startOfToday(), []);
    const timer = useTimer(montadoHoy);
    // `timer` va en las deps a propósito: si un pickup pagado se ve o no depende
    // de la hora, y así se refresca con el reloj de la pantalla (antes solo se
    // reevaluaba cuando llegaba un snapshot).
    const orders = useMemo(
        () => filterBoardOrders(rawOrders, { hideTodayLongTime, search: onSearch, now: new Date() }),
        [rawOrders, hideTodayLongTime, onSearch, timer]
    );
    // Avisa cuando un pedido se pasa de su tiempo. Vive aquí porque esta
    // pantalla está abierta todo el día en cocina y el server escala a cero: no
    // hay cron que pueda darse cuenta. Recibe TODAS las órdenes de la ventana:
    // un pedido retrasado lo está aunque el buscador lo esté ocultando.
    useDelayedOrdersNotifier(rawOrders);

    const unsubscribeRef = useRef<(() => void) | undefined>(undefined);
    const [filters, setFilters] = useState<OrdersDateRange>(() => {
        const start = startOfToday();
        return { startDate: start, endDate: addDays(start, 1) };
    });

    // Filtros de pantalla que el callback del listener necesita leer SIN estar en
    // sus deps.
    const hideTodayLongTimeRef = useRef(hideTodayLongTime);
    hideTodayLongTimeRef.current = hideTodayLongTime;
    const searchRef = useRef(onSearch);
    searchRef.current = onSearch;

    // La ventana de pedidos, COMPARTIDA entre montajes.
    //
    // ⚠️ Dos cosas que costaban lecturas y ya no:
    // ① las deps eran `[filters, …]`, el OBJETO — y el selector de fechas hace
    //   `setFilters({ ...filters, startDate })` y luego otro para `endDate`, así
    //   que un cambio de rango rearmaba los listeners DOS veces. Hoy las deps son
    //   los milisegundos: mismo rango, mismo efecto, aunque el objeto sea nuevo.
    // ② el listener nacía y moría con la pantalla, y la primera entrega de un
    //   `onSnapshot` trae la ventana COMPLETA (~180 pedidos): ir al POS a cobrar y
    //   volver la pagaba otra vez, decenas de veces al día.
    //   `subscribeOrdersWindow` mantiene la suscripción viva un rato y le entrega
    //   el último snapshot a quien vuelve, sin releer nada.
    //
    // El `unsubscribe` sigue en un `ref` y no en `useState`: con `useState` el
    // cleanup capturaba el valor del render anterior y cerraba el listener
    // equivocado — al salir de /orders el último quedaba vivo, cobrando cada
    // cambio de pedido, hasta recargar la pestaña.
    const startMillis = filters.startDate.getTime();
    const endMillis = filters.endDate.getTime();
    useEffect(() => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = undefined;

        if (branchOfficeSelected?.id) {
            unsubscribeRef.current = subscribeOrdersWindow(
                { startMillis, endMillis, branchOfficeId: branchOfficeSelected.id },
                (key, emit) =>
                    ordersService.getOrdersRealtime(
                        emit,
                        new Date(key.startMillis),
                        new Date(key.endMillis),
                        key.branchOfficeId
                    ),
                (ordersData) => {
                    const sorted = sortBoardOrders(ordersData);
                    setRawOrders(sorted);
                    // Sonido de pedido nuevo: la regla de siempre (solo sin búsqueda
                    // activa y solo sobre lo visible), evaluada aquí porque es un
                    // evento del snapshot, no del render.
                    if (searchRef.current === '') {
                        const visibles = filterBoardOrders(sorted, {
                            hideTodayLongTime: hideTodayLongTimeRef.current,
                            search: '',
                            now: new Date()
                        });
                        if (hasPaidDeliveryOrders(visibles)) {
                            playNotificationSound();
                        }
                    }
                }
            );
        }
        return () => {
            unsubscribeRef.current?.();
            unsubscribeRef.current = undefined;
        };
    }, [startMillis, endMillis, branchOfficeSelected?.id, ordersService]);

    useEffect(() => {
        const checkOrdersForNotification = () => {
            const now = new Date();
            const fiveMinutesLater = new Date(now.getTime() + 5 * 60000);

            const newPaidOrders = orders.filter((order) => {
                if (order.status !== OrderStatus.Paid || !order.deliveryType) return false;

                if (order.deliveryType === DeliveryType.Pickup) {
                    const orderDate = order.date.toDate();
                    const isToday = orderDate.toDateString() === now.toDateString();

                    return isToday && orderDate > now && orderDate <= fiveMinutesLater;
                }

                return false;
            });

            if (newPaidOrders.length > 0) {
                playNotificationSound();
            }
        };

        if (timer % 10 === 0) {
            checkOrdersForNotification();
        }
    }, [timer, orders]);

    return {
        orders,
        search,
        setSearch,
        hideTodayLongTime,
        setHideTodayLongTime,
        filters,
        setFilters
    };
};

export default useOrdersFeed;
